<template>
  <div class="criteria-list">
    <div
      v-for="criterion in criteria"
      :key="criterion.id"
      class="criterion-item"
      :class="getCriterionClass(criterion)"
    >
      <!-- Criterion Header -->
      <div class="criterion-header">
        <div class="criterion-info">
          <h3 class="criterion-title">{{ criterion.description }}</h3>
          <div class="criterion-meta">
            <span class="criterion-weight">Weight: {{ criterion.weight || 1 }}</span>
            <span class="criterion-max">Max: {{ criterion.maxScore }} points</span>
          </div>
        </div>
        
        <div class="criterion-actions">
          <button
            @click="toggleFlag(criterion.id)"
            class="action-btn flag-btn"
            :class="{ flagged: scores[criterion.id]?.flagged }"
            title="Flag for review"
          >
            🚩
          </button>
        </div>
      </div>
      
      <!-- Scoring Interface -->
      <div class="scoring-interface">
        <!-- Score Buttons -->
        <div class="score-buttons">
          <button
            v-for="score in getScoreOptions(criterion.maxScore)"
            :key="score"
            @click="setScore(criterion.id, score)"
            class="score-btn"
            :class="getScoreButtonClass(criterion.id, score)"
          >
            {{ score }}
          </button>
        </div>
        
        <!-- Current Score Display -->
        <div class="current-score">
          <span class="score-label">Score:</span>
          <span class="score-value">
            {{ scores[criterion.id]?.score ?? '–' }}/{{ criterion.maxScore }}
          </span>
        </div>
      </div>
      
      <!-- Comments Section -->
      <div class="comments-section">
        <textarea
          v-model="localComments[criterion.id]"
          @focus="setActive(criterion.id)"
          @blur="saveComments(criterion.id)"
          :placeholder="getCommentPlaceholder(criterion)"
          class="comment-textarea"
          :class="{ active: isCommenting === criterion.id }"
          rows="2"
        />
        
        <div v-if="scores[criterion.id]?.comments" class="comment-preview">
          {{ scores[criterion.id].comments }}
        </div>
      </div>
      
      <!-- Criterion Feedback (if provided) -->
      <div v-if="criterion.exampleFeedback" class="criterion-feedback">
        <strong>Example:</strong> {{ criterion.exampleFeedback }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    criteria: Array,
    scores: Object,
    isCommenting: String
  },
  
  data() {
    return {
      localComments: {}
    };
  },
  
  watch: {
    criteria: {
      handler(newCriteria) {
        // Initialize local comments
        newCriteria.forEach(criterion => {
          if (!this.localComments[criterion.id]) {
            this.localComments[criterion.id] = this.scores[criterion.id]?.comments || '';
          }
        });
      },
      immediate: true
    }
  },
  
  methods: {
    getScoreOptions(maxScore) {
      return Array.from({ length: maxScore + 1 }, (_, i) => i);
    },
    
    getScoreButtonClass(criterionId, score) {
      const currentScore = this.scores[criterionId]?.score;
      const classes = [];
      
      if (currentScore === score) {
        classes.push('active');
        
        // Color coding based on score percentage
        const percentage = (score / this.getCriterionMaxScore(criterionId)) * 100;
        if (percentage >= 80) classes.push('excellent');
        else if (percentage >= 60) classes.push('good');
        else if (percentage >= 40) classes.push('average');
        else classes.push('poor');
      }
      
      return classes.join(' ');
    },
    
    getCriterionClass(criterion) {
      const classes = [];
      const score = this.scores[criterion.id]?.score;
      
      if (score !== undefined && score !== null) {
        classes.push('graded');
      }
      
      if (this.scores[criterion.id]?.flagged) {
        classes.push('flagged');
      }
      
      return classes.join(' ');
    },
    
    getCriterionMaxScore(criterionId) {
      const criterion = this.criteria.find(c => c.id === criterionId);
      return criterion ? criterion.maxScore : 0;
    },
    
    getCommentPlaceholder(criterion) {
      return `Add comments for ${criterion.description} (optional)...`;
    },
    
    setScore(criterionId, score) {
      this.$emit('score-change', {
        criterionId,
        score,
        comments: this.localComments[criterionId]
      });
    },
    
    setActive(criterionId) {
      this.$emit('comment-focus', criterionId);
    },
    
    saveComments(criterionId) {
      this.$emit('comment-blur', criterionId, this.localComments[criterionId]);
      this.$emit('score-change', {
        criterionId,
        comments: this.localComments[criterionId]
      });
    },
    
    toggleFlag(criterionId) {
      const currentlyFlagged = this.scores[criterionId]?.flagged || false;
      this.$emit('score-change', {
        criterionId,
        flagged: !currentlyFlagged
      });
    }
  }
}
</script>

<style scoped>
.criteria-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.criterion-item {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  transition: all 0.2s ease;
}

.criterion-item.graded {
  background: #f0f9ff;
  border-color: #b3e0ff;
}

.criterion-item.flagged {
  border-left: 4px solid #dc3545;
}

.criterion-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
}

.criterion-info {
  flex: 1;
}

.criterion-title {
  font-size: 1.1em;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 8px 0;
  line-height: 1.4;
}

.criterion-meta {
  display: flex;
  gap: 15px;
  font-size: 0.85em;
  color: #6c757d;
}

.criterion-actions {
  flex-shrink: 0;
}

.action-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.flag-btn:hover {
  background: #ffeaa7;
}

.flag-btn.flagged {
  background: #f8d7da;
}

.scoring-interface {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  gap: 20px;
}

.score-buttons {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
}

.score-btn {
  width: 40px;
  height: 40px;
  border: 2px solid #dee2e6;
  border-radius: 6px;
  background: white;
  color: #495057;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.score-btn:hover {
  background: #e9ecef;
  border-color: #adb5bd;
}

.score-btn.active {
  color: white;
  border-color: transparent;
}

.score-btn.active.excellent { background: #28a745; }
.score-btn.active.good { background: #17a2b8; }
.score-btn.active.average { background: #ffc107; color: #212529; }
.score-btn.active.poor { background: #dc3545; }

.current-score {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 80px;
}

.score-label {
  font-size: 0.8em;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.score-value {
  font-size: 1.2em;
  font-weight: 600;
  color: #495057;
}

.comments-section {
  margin-bottom: 10px;
}

.comment-textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 0.9em;
  resize: vertical;
  transition: all 0.2s ease;
}

.comment-textarea:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0,123,255,0.25);
}

.comment-textarea.active {
  border-color: #007bff;
}

.comment-preview {
  background: white;
  padding: 8px 12px;
  border-radius: 4px;
  border-left: 3px solid #007bff;
  font-size: 0.9em;
  margin-top: 5px;
}

.criterion-feedback {
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 4px;
  padding: 10px;
  font-size: 0.85em;
  color: #856404;
}
</style>